/**
 * Mock data for authentication endpoints
 */

import { AuthResponse, LoginResponse, UserResponse, LogoutResponse, OAuthProvider } from '@/types/auth';
import { getMockProviderBySlug } from '../providers';

// Mock OTP codes for testing
const VALID_OTP = '123456';

// Mock auth tokens
const generateMockToken = (userId: string, type: 'provider' | 'customer') => {
  return `mock_${type}_token_${userId}_${Date.now()}`;
};

const providerForMocks = getMockProviderBySlug('tech-solutions')?.provider || {
  id: '1',
  slug: 'tech-solutions',
  name: 'Tech Solutions Ltd.',
  email: 'contact@tech-solutions.com',
  status: 'active',
  created_at: '2024-01-01T00:00:00Z',
  updated_at: new Date().toISOString(),
};

const buildMockOAuthResponse = (scope: 'provider' | 'customer', provider: OAuthProvider): AuthResponse => {
  const timestamp = new Date().toISOString();
  const userEmail =
    scope === 'provider'
      ? `${provider}.admin@${providerForMocks.slug}.com`
      : `${provider}.customer@${providerForMocks.slug}.com`;

  const baseName = `${provider.charAt(0).toUpperCase()}${provider.slice(1)} OAuth`;
  const user =
    scope === 'provider'
      ? {
          id: `oauth-provider-${provider}`,
          email: userEmail,
          name: `${baseName} Admin`,
          role: 'provider_admin' as const,
          provider_id: providerForMocks.id,
          created_at: '2024-01-01T00:00:00Z',
          updated_at: timestamp,
        }
      : {
          id: `oauth-customer-${provider}`,
          email: userEmail,
          name: `${baseName} Customer`,
          role: 'customer' as const,
          provider_id: providerForMocks.id,
          created_at: '2024-02-01T00:00:00Z',
          updated_at: timestamp,
        };

  return {
    success: true,
    message: 'Authentication successful',
    data: {
      user,
      provider: providerForMocks,
      token: generateMockToken(user.id, scope),
      expires_at: new Date(Date.now() + (scope === 'provider' ? 7 : 1) * 24 * 60 * 60 * 1000).toISOString(),
    },
  };
};

// Mock responses
export const authMockData = {
  // Provider auth endpoints
  '/api/v1/auth/login': (data: { email: string }): LoginResponse => {
    console.log('[Mock] Provider login OTP sent to:', data.email);
    return {
      success: true,
      message: 'OTP sent successfully',
      data: {
        expires_at: new Date(Date.now() + 5 * 60 * 1000).toISOString(), // 5 minutes
      },
    };
  },

  '/api/v1/auth/verify': (data: { email: string; code: string }): AuthResponse => {
    if (data.code !== VALID_OTP) {
      throw {
        response: {
          status: 422,
          data: {
            message: 'Invalid OTP code',
            errors: {
              code: ['The provided OTP is invalid or expired'],
            },
          },
        },
      };
    }

    // Get provider based on email domain or default
    const emailDomain = data.email.split('@')[1];
    const providerSlug = emailDomain?.includes('tech-solutions') ? 'tech-solutions' : 'acme-corp';
    const mockProvider = getMockProviderBySlug(providerSlug);

    const user = {
      id: '1',
      email: data.email,
      name: data.email.split('@')[0].replace('.', ' ').replace(/\b\w/g, l => l.toUpperCase()),
      role: 'provider_admin' as const,
      provider_id: mockProvider?.provider.id || '1',
      created_at: '2024-01-01T00:00:00Z',
      updated_at: new Date().toISOString(),
    };

    return {
      success: true,
      message: 'Authentication successful',
      data: {
        user,
        provider: mockProvider?.provider || {
          id: '1',
          slug: 'default-provider',
          name: 'Default Provider',
          email: 'contact@default.com',
          status: 'active',
          created_at: '2024-01-01T00:00:00Z',
          updated_at: new Date().toISOString(),
        },
        token: generateMockToken(user.id, 'provider'),
        expires_at: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString(), // 7 days
      },
    };
  },

  '/api/v1/auth/user': (): UserResponse => {
    return {
      success: true,
      data: {
        user: {
          id: '1',
          email: 'admin@tech-solutions.com',
          name: 'Admin Tech Solutions',
          role: 'provider_admin',
          provider_id: '1',
          created_at: '2024-01-01T00:00:00Z',
          updated_at: new Date().toISOString(),
        },
        provider: getMockProviderBySlug('tech-solutions')?.provider || {
          id: '1',
          slug: 'tech-solutions',
          name: 'Tech Solutions Ltd.',
          email: 'contact@tech-solutions.com',
          status: 'active',
          created_at: '2024-01-01T00:00:00Z',
          updated_at: new Date().toISOString(),
        },
      },
    };
  },

  '/api/v1/auth/logout': (): LogoutResponse => {
    return {
      success: true,
      message: 'Logged out successfully',
    };
  },

  '/api/v1/auth/provider/oauth/google/mock': () => buildMockOAuthResponse('provider', 'google'),
  '/api/v1/auth/provider/oauth/github/mock': () => buildMockOAuthResponse('provider', 'github'),
  '/api/v1/auth/provider/oauth/apple/mock': () => buildMockOAuthResponse('provider', 'apple'),
  '/api/v1/auth/provider/oauth/azure/mock': () => buildMockOAuthResponse('provider', 'azure'),
  '/api/v1/auth/provider/oauth/providers': () => ({
    success: true,
    data: {
      providers: [
        { provider: 'google', enabled: true, mode: 'live' },
        { provider: 'github', enabled: true, mode: 'test' },
        { provider: 'apple', enabled: false },
        { provider: 'azure', enabled: true, mode: 'live', label: 'Microsoft', icon: 'microsoft' },
      ],
    },
  }),
  '/api/v1/auth/provider/password-login': () => ({
    data: {
      password_login_enabled: true,
      password_policy: {
        min_length: 12,
        require_numbers: true,
        require_symbols: true,
        require_mixed_case: true,
        disallow_previous: 5,
      },
    },
    jsonapi: {
      version: '1.0',
    },
  }),
  '/api/v1/auth/provider/forgot-password': () => ({
    data: [],
    meta: {
      message: 'If an account exists for that email, a reset link has been sent.',
    },
    jsonapi: {
      version: '1.0',
    },
  }),
  '/api/v1/auth/provider/reset-password': () => ({
    data: [],
    meta: {
      message: 'Password reset successfully.',
    },
    jsonapi: {
      version: '1.0',
    },
  }),

  // Customer auth endpoints
  '/api/v1/auth/customer/login': (data: { email: string }): LoginResponse => {
    console.log('[Mock] Customer login OTP sent to:', data.email);
    return {
      success: true,
      message: 'OTP sent successfully',
      data: {
        expires_at: new Date(Date.now() + 5 * 60 * 1000).toISOString(),
      },
    };
  },

  '/api/v1/auth/customer/verify': (data: { email: string; code: string }): AuthResponse => {
    if (data.code !== VALID_OTP) {
      throw {
        response: {
          status: 422,
          data: {
            message: 'Invalid OTP code',
            errors: {
              code: ['The provided OTP is invalid or expired'],
            },
          },
        },
      };
    }

    const user = {
      id: '100',
      email: data.email,
      name: data.email.split('@')[0].replace('.', ' ').replace(/\b\w/g, l => l.toUpperCase()),
      role: 'customer' as const,
      provider_id: '1',
      created_at: '2024-02-01T00:00:00Z',
      updated_at: new Date().toISOString(),
    };

    return {
      success: true,
      message: 'Authentication successful',
      data: {
        user,
        provider: getMockProviderBySlug('tech-solutions')?.provider || {
          id: '1',
          slug: 'tech-solutions',
          name: 'Tech Solutions Ltd.',
          email: 'contact@tech-solutions.com',
          status: 'active',
          created_at: '2024-01-01T00:00:00Z',
          updated_at: new Date().toISOString(),
        },
        token: generateMockToken(user.id, 'customer'),
        expires_at: new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString(),
      },
    };
  },
  '/api/v1/auth/customer/password': (data?: { data?: { type?: string } }) => {
    const type = data?.data?.type;
    const message = type === 'customer-password-sets'
      ? 'Password set successfully'
      : 'Password updated successfully';

    return {
      meta: {
        message,
      },
      jsonapi: {
        version: '1.0',
      },
    };
  },

  '/api/v1/auth/customer/user': (): UserResponse => {
    const provider = getMockProviderBySlug('tech-solutions')?.provider || {
      id: '1',
      slug: 'tech-solutions',
      name: 'Tech Solutions Ltd.',
      domain: 'tech-solutions.app',
      customDomains: [],
      createdAt: '2024-01-01T00:00:00Z',
      updatedAt: new Date().toISOString(),
    };

    return {
      success: true,
      data: {
        customer: {
          id: '100',
          email: 'customer@example.com',
          name: 'Customer Example',
          role: 'customer',
          provider_id: '1',
          hasPassword: true,
          created_at: '2024-02-01T00:00:00Z',
          updated_at: new Date().toISOString(),
          roles: [],
          permissions: [],
        },
        provider,
      },
      meta: {
        message: 'Customer retrieved successfully',
        customer: {
          id: '100',
          email: 'customer@example.com',
          name: 'Customer Example',
          role: 'customer',
          provider_id: '1',
          hasPassword: true,
          created_at: '2024-02-01T00:00:00Z',
          updated_at: new Date().toISOString(),
          roles: [],
          permissions: [],
        },
        provider,
      },
      jsonapi: {
        version: '1.0',
      },
    };
  },

  '/api/v1/auth/customer/oauth/google/mock': () => buildMockOAuthResponse('customer', 'google'),
  '/api/v1/auth/customer/oauth/github/mock': () => buildMockOAuthResponse('customer', 'github'),
  '/api/v1/auth/customer/oauth/apple/mock': () => buildMockOAuthResponse('customer', 'apple'),
  '/api/v1/auth/customer/oauth/azure/mock': () => buildMockOAuthResponse('customer', 'azure'),
  '/api/v1/auth/customer/oauth/providers': () => ({
    success: true,
    data: {
      providers: [
        { provider: 'google', enabled: true, mode: 'live' },
        { provider: 'github', enabled: true, mode: 'live' },
        { provider: 'apple', enabled: true, mode: 'test' },
        { provider: 'azure', enabled: true, mode: 'live', label: 'Microsoft', icon: 'microsoft' },
      ],
    },
  }),
  '/api/v1/auth/customer/password-login': () => ({
    data: {
      password_login_enabled: true,
      password_policy: {
        min_length: 12,
        require_numbers: true,
        require_symbols: true,
        require_mixed_case: true,
        disallow_previous: 5,
      },
    },
    jsonapi: {
      version: '1.0',
    },
  }),
  '/api/v1/auth/customer/forgot-password': () => ({
    data: [],
    meta: {
      message: 'If an account exists for that email, a reset link has been sent.',
    },
    jsonapi: {
      version: '1.0',
    },
  }),
  '/api/v1/auth/customer/reset-password': () => ({
    data: [],
    meta: {
      message: 'Password reset successfully.',
    },
    jsonapi: {
      version: '1.0',
    },
  }),

  '/api/v1/auth/customer/logout': (): LogoutResponse => {
    return {
      success: true,
      message: 'Logged out successfully',
    };
  },
};
